import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Baby, Users, GraduationCap, Briefcase, Heart, Settings, Building2, TrendingUp, RefreshCw } from 'lucide-react';
import { apiClient as api } from '../../lib/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell, ReferenceLine } from 'recharts';

const GRUPOS_ICONS = {
  bebes: Baby,
  criancas: Users,
  jovens: GraduationCap,
  adultos: Briefcase,
  idosos: Heart
};

const GRUPOS_COLORS = {
  bebes: 'bg-pink-100 text-pink-700',
  criancas: 'bg-blue-100 text-blue-700',
  jovens: 'bg-green-100 text-green-700',
  adultos: 'bg-orange-100 text-orange-700',
  idosos: 'bg-purple-100 text-purple-700'
};

export default function ConfiguracaoDemografiaPage() {
  const [faixas, setFaixas] = useState([]);
  const [grupos, setGrupos] = useState({});
  const [valencias, setValencias] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [editingFaixa, setEditingFaixa] = useState(null);
  const [selectedValencia, setSelectedValencia] = useState(null);
  
  // Pyramid visualization state
  const [instituicoes, setInstituicoes] = useState([]);
  const [selectedInstituicaoId, setSelectedInstituicaoId] = useState('__all__');
  const [pyramidData, setPyramidData] = useState([]);
  const [pyramidStats, setPyramidStats] = useState({ total: 0, masculino: 0, feminino: 0 });
  const [loadingPyramid, setLoadingPyramid] = useState(false);
  
  const [formData, setFormData] = useState({
    codigo: '',
    nome: '',
    idade_min: 0,
    idade_max: 0,
    grupo: 'adultos',
    ordem: 1,
    ativo: true
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [faixasRes, gruposRes, valenciasRes, mappingsRes, instRes] = await Promise.all([
        api.get('/demografia/faixas-etarias'),
        api.get('/demografia/faixas-etarias/grupos'),
        api.get('/valencias'),
        api.get('/demografia/valencias-mapping'),
        api.get('/instituicoes?limit=500')
      ]);
      setFaixas(faixasRes.data);
      setGrupos(gruposRes.data);
      setValencias(valenciasRes.data);
      setMappings(mappingsRes.data);
      setInstituicoes(instRes.data || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Load pyramid data when institution or faixas change
  const loadPyramidData = useCallback(async () => {
    if (faixas.length === 0) return;
    
    setLoadingPyramid(true);
    try {
      const params = selectedInstituicaoId !== '__all__' 
        ? `?instituicao_id=${selectedInstituicaoId}` 
        : '';
      const response = await api.get(`/demografia/estatisticas/piramide${params}`);
      const data = response.data;
      
      // Transform data for pyramid chart (negative values for left side)
      const chartData = data.piramide.map(p => ({
        faixa: p.faixa,
        codigo: p.codigo,
        grupo: p.grupo,
        masculino: -p.masculino, // Negative for left side of pyramid
        feminino: p.feminino,
        masculinoReal: p.masculino,
        femininoReal: p.feminino
      })).reverse(); // Reverse so younger are at bottom
      
      setPyramidData(chartData);
      setPyramidStats({
        total: data.total_geral,
        masculino: data.total_masculino,
        feminino: data.total_feminino
      });
    } catch (error) {
      console.error('Erro ao carregar pirâmide:', error);
      // Create empty pyramid from faixas
      const emptyData = faixas.sort((a, b) => b.ordem - a.ordem).map(f => ({
        faixa: f.nome,
        codigo: f.codigo,
        grupo: f.grupo,
        masculino: 0,
        feminino: 0,
        masculinoReal: 0,
        femininoReal: 0
      }));
      setPyramidData(emptyData);
      setPyramidStats({ total: 0, masculino: 0, feminino: 0 });
    } finally {
      setLoadingPyramid(false);
    }
  }, [faixas, selectedInstituicaoId]);
  
  useEffect(() => {
    if (faixas.length > 0) {
      loadPyramidData();
    }
  }, [faixas, selectedInstituicaoId, loadPyramidData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFaixa) {
        await api.put(`/demografia/faixas-etarias/${editingFaixa.id}`, formData);
        toast.success('Faixa etária atualizada');
      } else {
        await api.post('/demografia/faixas-etarias', formData);
        toast.success('Faixa etária criada');
      }
      setDialogOpen(false);
      setEditingFaixa(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao guardar');
    }
  };

  const handleEdit = (faixa) => {
    setEditingFaixa(faixa);
    setFormData({
      codigo: faixa.codigo,
      nome: faixa.nome,
      idade_min: faixa.idade_min,
      idade_max: faixa.idade_max,
      grupo: faixa.grupo,
      ordem: faixa.ordem,
      ativo: faixa.ativo
    });
    setDialogOpen(true);
  };

  const handleDelete = async (faixaId) => {
    if (!window.confirm('Desativar esta faixa etária?')) return;
    try {
      await api.delete(`/demografia/faixas-etarias/${faixaId}`);
      toast.success('Faixa etária desativada');
      fetchData();
    } catch (error) {
      toast.error('Erro ao desativar');
    }
  };

  const resetForm = () => {
    setFormData({
      codigo: '',
      nome: '',
      idade_min: 0,
      idade_max: 0,
      grupo: 'adultos',
      ordem: faixas.length + 1,
      ativo: true
    });
  };

  const openMappingDialog = (valencia) => {
    setSelectedValencia(valencia);
    setMappingDialogOpen(true);
  };

  const handleMappingChange = async (faixaId, checked) => {
    if (!selectedValencia) return;
    
    const mapping = mappings.find(m => m.valencia_id === selectedValencia.id);
    const currentFaixas = mapping?.faixas_etarias_ids || [];
    
    const newFaixas = checked 
      ? [...currentFaixas, faixaId]
      : currentFaixas.filter(id => id !== faixaId);
    
    try {
      await api.post(`/demografia/valencias/${selectedValencia.id}/faixas-etarias`, {
        valencia_id: selectedValencia.id,
        faixas_etarias_ids: newFaixas,
        tem_demografia: true,
        campos_obrigatorios: []
      });
      toast.success('Mapeamento atualizado');
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar mapeamento');
    }
  };

  const getValenciaFaixas = (valenciaId) => {
    const mapping = mappings.find(m => m.valencia_id === valenciaId);
    return mapping?.faixas_etarias_ids || [];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="config-demografia-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configuração de Demografia</h1>
          <p className="text-gray-500 mt-1">Configure faixas etárias e mapeamentos para respostas sociais</p>
        </div>
      </div>

      <Tabs defaultValue="faixas" className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-[500px]">
          <TabsTrigger value="faixas" data-testid="tab-faixas">Faixas Etárias</TabsTrigger>
          <TabsTrigger value="mappings" data-testid="tab-mappings">Mapeamentos</TabsTrigger>
          <TabsTrigger value="preview" data-testid="tab-preview">Pré-visualização</TabsTrigger>
        </TabsList>

        {/* TAB: Faixas Etárias */}
        <TabsContent value="faixas" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">{faixas.length} faixas etárias configuradas</p>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={resetForm} data-testid="btn-nova-faixa">
                  <Plus className="w-4 h-4 mr-2" /> Nova Faixa
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>{editingFaixa ? 'Editar' : 'Nova'} Faixa Etária</DialogTitle>
                  <DialogDescription>Configure os intervalos de idade para a pirâmide etária</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="codigo">Código</Label>
                      <Input
                        id="codigo"
                        value={formData.codigo}
                        onChange={(e) => setFormData({...formData, codigo: e.target.value})}
                        placeholder="ex: adultos_35_44a"
                        required
                        disabled={!!editingFaixa}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nome">Nome</Label>
                      <Input
                        id="nome"
                        value={formData.nome}
                        onChange={(e) => setFormData({...formData, nome: e.target.value})}
                        placeholder="ex: Adultos (35-44 anos)"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="idade_min">Idade Mínima</Label>
                      <Input
                        id="idade_min"
                        type="number"
                        min="0"
                        max="120"
                        value={formData.idade_min}
                        onChange={(e) => setFormData({...formData, idade_min: parseInt(e.target.value)})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="idade_max">Idade Máxima</Label>
                      <Input
                        id="idade_max"
                        type="number"
                        min="0"
                        max="120"
                        value={formData.idade_max}
                        onChange={(e) => setFormData({...formData, idade_max: parseInt(e.target.value)})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ordem">Ordem</Label>
                      <Input
                        id="ordem"
                        type="number"
                        min="1"
                        value={formData.ordem}
                        onChange={(e) => setFormData({...formData, ordem: parseInt(e.target.value)})}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="grupo">Grupo</Label>
                    <Select
                      value={formData.grupo}
                      onValueChange={(value) => setFormData({...formData, grupo: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bebes">Bebés</SelectItem>
                        <SelectItem value="criancas">Crianças</SelectItem>
                        <SelectItem value="jovens">Jovens</SelectItem>
                        <SelectItem value="adultos">Adultos</SelectItem>
                        <SelectItem value="idosos">Idosos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="ativo"
                      checked={formData.ativo}
                      onCheckedChange={(checked) => setFormData({...formData, ativo: checked})}
                    />
                    <Label htmlFor="ativo">Ativo</Label>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button type="submit" data-testid="btn-guardar-faixa">Guardar</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Faixas por Grupo */}
          <div className="grid gap-6">
            {Object.entries(grupos).map(([grupoKey, grupoData]) => {
              const Icon = GRUPOS_ICONS[grupoKey] || Users;
              return (
                <Card key={grupoKey}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Icon className="w-5 h-5" />
                      <CardTitle className="text-lg">{grupoData.nome}</CardTitle>
                      <Badge variant="secondary">{grupoData.faixas.length} faixas</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nome</TableHead>
                          <TableHead>Intervalo</TableHead>
                          <TableHead>Código</TableHead>
                          <TableHead>Ordem</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {grupoData.faixas.map((faixa) => (
                          <TableRow key={faixa.id}>
                            <TableCell className="font-medium">{faixa.nome}</TableCell>
                            <TableCell>
                              <Badge className={GRUPOS_COLORS[grupoKey]}>
                                {faixa.idade_min} - {faixa.idade_max} anos
                              </Badge>
                            </TableCell>
                            <TableCell className="text-gray-500 text-sm">{faixa.codigo}</TableCell>
                            <TableCell>{faixa.ordem}</TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="sm" onClick={() => handleEdit(faixa)}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(faixa.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* TAB: Mapeamentos */}
        <TabsContent value="mappings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Mapeamento Respostas Sociais → Faixas Etárias</CardTitle>
              <CardDescription>
                Configure quais faixas etárias são relevantes para cada resposta social.
                Por defeito, as faixas são determinadas pela categoria da resposta social.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Resposta Social</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Faixas Etárias</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {valencias.map((valencia) => {
                    const valenciaFaixas = getValenciaFaixas(valencia.id);
                    return (
                      <TableRow key={valencia.id}>
                        <TableCell className="font-medium">{valencia.nome}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{valencia.categoria || 'Geral'}</Badge>
                        </TableCell>
                        <TableCell>
                          {valenciaFaixas.length > 0 ? (
                            <span className="text-sm text-gray-600">
                              {valenciaFaixas.length} faixas personalizadas
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400">Usa padrão da categoria</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="outline" size="sm" onClick={() => openMappingDialog(valencia)}>
                            <Settings className="w-4 h-4 mr-2" /> Configurar
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Dialog de Mapeamento */}
          <Dialog open={mappingDialogOpen} onOpenChange={setMappingDialogOpen}>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>Configurar Faixas Etárias</DialogTitle>
                <DialogDescription>
                  Selecione as faixas etárias disponíveis para a resposta social "{selectedValencia?.nome}"
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 max-h-[400px] overflow-y-auto">
                {Object.entries(grupos).map(([grupoKey, grupoData]) => (
                  <div key={grupoKey} className="space-y-2">
                    <h4 className="font-medium text-sm text-gray-700">{grupoData.nome}</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {grupoData.faixas.map((faixa) => {
                        const isSelected = selectedValencia && getValenciaFaixas(selectedValencia.id).includes(faixa.id);
                        return (
                          <div key={faixa.id} className="flex items-center space-x-2 p-2 bg-gray-50 rounded">
                            <Switch
                              id={`faixa-${faixa.id}`}
                              checked={isSelected}
                              onCheckedChange={(checked) => handleMappingChange(faixa.id, checked)}
                            />
                            <Label htmlFor={`faixa-${faixa.id}`} className="text-sm cursor-pointer">
                              {faixa.nome}
                            </Label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button onClick={() => setMappingDialogOpen(false)}>Fechar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* TAB: Pré-visualização Interativa */}
        <TabsContent value="preview" className="space-y-6">
          {/* Filtros e Estatísticas */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Seletor de Instituição */}
            <Card className="lg:col-span-1">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Filtrar por Instituição
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Select value={selectedInstituicaoId} onValueChange={setSelectedInstituicaoId}>
                  <SelectTrigger data-testid="select-instituicao-piramide">
                    <SelectValue placeholder="Todas as instituições" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todas as instituições</SelectItem>
                    {instituicoes.map(inst => (
                      <SelectItem key={inst.id} value={inst.id}>
                        {inst.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full mt-3"
                  onClick={loadPyramidData}
                  disabled={loadingPyramid}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingPyramid ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
              </CardContent>
            </Card>
            
            {/* Estatísticas Resumidas */}
            <Card className="bg-gradient-to-br from-slate-50 to-slate-100">
              <CardContent className="pt-6">
                <div className="text-center">
                  <Users className="w-8 h-8 mx-auto text-slate-600 mb-2" />
                  <p className="text-3xl font-bold text-slate-900">{pyramidStats.total.toLocaleString('pt-PT')}</p>
                  <p className="text-sm text-slate-500">Total de Pessoas</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
              <CardContent className="pt-6">
                <div className="text-center">
                  <TrendingUp className="w-8 h-8 mx-auto text-blue-600 mb-2" />
                  <p className="text-3xl font-bold text-blue-700">{pyramidStats.masculino.toLocaleString('pt-PT')}</p>
                  <p className="text-sm text-blue-600">Masculino</p>
                  <Badge className="mt-1 bg-blue-200 text-blue-800">
                    {pyramidStats.total > 0 ? ((pyramidStats.masculino / pyramidStats.total) * 100).toFixed(1) : 0}%
                  </Badge>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-pink-50 to-pink-100">
              <CardContent className="pt-6">
                <div className="text-center">
                  <Heart className="w-8 h-8 mx-auto text-pink-600 mb-2" />
                  <p className="text-3xl font-bold text-pink-700">{pyramidStats.feminino.toLocaleString('pt-PT')}</p>
                  <p className="text-sm text-pink-600">Feminino</p>
                  <Badge className="mt-1 bg-pink-200 text-pink-800">
                    {pyramidStats.total > 0 ? ((pyramidStats.feminino / pyramidStats.total) * 100).toFixed(1) : 0}%
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Pirâmide Etária Interativa */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Pirâmide Etária Interativa
              </CardTitle>
              <CardDescription>
                Visualização da distribuição demográfica por faixa etária e género.
                Clique nas barras para ver detalhes.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingPyramid ? (
                <div className="flex items-center justify-center h-[500px]">
                  <div className="w-8 h-8 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
                </div>
              ) : pyramidData.length > 0 ? (
                <div className="h-[500px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      layout="vertical"
                      data={pyramidData}
                      margin={{ top: 20, right: 30, left: 100, bottom: 5 }}
                      stackOffset="sign"
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis 
                        type="number" 
                        tickFormatter={(value) => Math.abs(value).toLocaleString('pt-PT')}
                        tick={{ fill: '#64748B', fontSize: 12 }}
                        domain={['dataMin', 'dataMax']}
                      />
                      <YAxis 
                        type="category" 
                        dataKey="faixa" 
                        tick={{ fill: '#64748B', fontSize: 11 }}
                        width={95}
                      />
                      <Tooltip 
                        formatter={(value, name) => [
                          Math.abs(value).toLocaleString('pt-PT'),
                          name === 'masculino' ? 'Masculino' : 'Feminino'
                        ]}
                        labelFormatter={(label) => `Faixa: ${label}`}
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #E2E8F0',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        }}
                      />
                      <Legend 
                        formatter={(value) => value === 'masculino' ? 'Masculino' : 'Feminino'}
                      />
                      <ReferenceLine x={0} stroke="#94A3B8" strokeWidth={2} />
                      <Bar 
                        dataKey="masculino" 
                        name="masculino" 
                        stackId="stack"
                        radius={[4, 0, 0, 4]}
                      >
                        {pyramidData.map((entry, index) => (
                          <Cell 
                            key={`cell-m-${index}`} 
                            fill={
                              entry.grupo === 'bebes' ? '#3B82F6' :
                              entry.grupo === 'criancas' ? '#0EA5E9' :
                              entry.grupo === 'jovens' ? '#06B6D4' :
                              entry.grupo === 'adultos' ? '#0284C7' :
                              '#1E40AF'
                            }
                          />
                        ))}
                      </Bar>
                      <Bar 
                        dataKey="feminino" 
                        name="feminino" 
                        stackId="stack"
                        radius={[0, 4, 4, 0]}
                      >
                        {pyramidData.map((entry, index) => (
                          <Cell 
                            key={`cell-f-${index}`} 
                            fill={
                              entry.grupo === 'bebes' ? '#EC4899' :
                              entry.grupo === 'criancas' ? '#F472B6' :
                              entry.grupo === 'jovens' ? '#FB7185' :
                              entry.grupo === 'adultos' ? '#F43F5E' :
                              '#BE185D'
                            }
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[400px] text-slate-500">
                  <Users className="w-16 h-16 mb-4 opacity-30" />
                  <p className="text-lg font-medium">Sem dados demográficos</p>
                  <p className="text-sm">Configure as faixas etárias e registe dados de utentes</p>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Tabela de Detalhe por Faixa */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Detalhe por Faixa Etária</CardTitle>
              <CardDescription>Valores absolutos e percentuais por grupo</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Faixa Etária</TableHead>
                    <TableHead>Grupo</TableHead>
                    <TableHead className="text-center text-blue-600">Masculino</TableHead>
                    <TableHead className="text-center text-pink-600">Feminino</TableHead>
                    <TableHead className="text-center">Total</TableHead>
                    <TableHead className="text-center">% do Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pyramidData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                        Sem dados para exibir
                      </TableCell>
                    </TableRow>
                  ) : (
                    pyramidData.map((row, idx) => {
                      const total = row.masculinoReal + row.femininoReal;
                      const pct = pyramidStats.total > 0 ? ((total / pyramidStats.total) * 100) : 0;
                      return (
                        <TableRow key={idx} className="hover:bg-slate-50">
                          <TableCell className="font-medium">{row.faixa}</TableCell>
                          <TableCell>
                            <Badge className={GRUPOS_COLORS[row.grupo]}>
                              {row.grupo.charAt(0).toUpperCase() + row.grupo.slice(1)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center text-blue-600 font-medium">
                            {row.masculinoReal.toLocaleString('pt-PT')}
                          </TableCell>
                          <TableCell className="text-center text-pink-600 font-medium">
                            {row.femininoReal.toLocaleString('pt-PT')}
                          </TableCell>
                          <TableCell className="text-center font-bold">
                            {total.toLocaleString('pt-PT')}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-blue-500 to-pink-500 rounded-full"
                                  style={{ width: `${Math.min(pct * 2, 100)}%` }}
                                />
                              </div>
                              <span className="text-sm text-slate-600">{pct.toFixed(1)}%</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          
          {/* Estrutura Visual Simplificada */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Estrutura de Faixas Configuradas</CardTitle>
              <CardDescription>Representação visual das {faixas.length} faixas etárias configuradas</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center space-y-1">
                {faixas.sort((a, b) => b.ordem - a.ordem).map((faixa) => {
                  const width = 100 - ((faixa.ordem - 1) * 5);
                  return (
                    <div
                      key={faixa.id}
                      className={`flex items-center justify-center py-2 text-sm font-medium rounded transition-all hover:scale-105 cursor-pointer ${GRUPOS_COLORS[faixa.grupo]}`}
                      style={{ width: `${width}%` }}
                      title={`${faixa.nome} (${faixa.idade_min}-${faixa.idade_max} anos)`}
                    >
                      {faixa.nome}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
